/**
 * useAuditHistory Hook
 *
 * Fetches engagement audit history via the EngagementService
 * (GET /api/Engagement/{recordId}/history) rather than the Audit API directly. The
 * EngagementService gates this endpoint on pay-rate visibility, so History (which contains
 * sensitive pay-rate changes) is only returned to users permitted to view it.
 */

import { useState, useEffect } from 'react'
import { useApi } from '@/lib/api'
import type { AuditHistoryDocument, EngagementHistoryResponse } from '@/types/api/audit'
import type { EngagementReferences } from '@/types/api/engagement'

const EMPTY_REFERENCES: EngagementReferences = { profiles: {}, organizations: {}, engagements: {} }

export interface UseAuditHistoryParams {
  recordId: number // The engagement business ID
  enabled?: boolean // Whether to fetch data (default: true)
}

export interface UseAuditHistoryResult {
  auditHistory: AuditHistoryDocument[]
  /** Server-resolved display info for the profiles/orgs referenced in the history (RLS-independent). */
  references: EngagementReferences
  loading: boolean
  error: Error | null
}

/**
 * Fetches audit history for a given grouping ID
 * 
 * @param params - Query parameters
 * @returns Audit history data, loading state, and error
 */
export function useAuditHistory({
  recordId,
  enabled = true,
}: UseAuditHistoryParams): UseAuditHistoryResult {
  const { engagementApi } = useApi()
  const [auditHistory, setAuditHistory] = useState<AuditHistoryDocument[]>([])
  const [references, setReferences] = useState<EngagementReferences>(EMPTY_REFERENCES)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled || !recordId) {
      setLoading(false)
      setAuditHistory([])
      setReferences(EMPTY_REFERENCES)
      return
    }

    let cancelled = false

    async function fetchAuditHistory() {
      setLoading(true)
      setError(null)

      try {
        // Call EngagementService: GET /api/Engagement/{recordId}/history (pay-rate gated). Returns the
        // documents plus server-resolved references (profile/org names) so the diff view can render
        // names even for a restricted caller who can't resolve them via RLS-gated lookups.
        const response = await engagementApi.get<EngagementHistoryResponse>(
          `/api/Engagement/${recordId}/history`
        )

        if (cancelled) return

        // Sort by version (newest first), then by createdDateTime (newest first)
        const sortedData = [...response.documents].sort((a, b) => {
          // Primary sort: version descending
          if (b.entityVersion !== a.entityVersion) {
            return b.entityVersion - a.entityVersion
          }
          // Secondary sort: createdDateTime descending
          return new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()
        })

        setAuditHistory(sortedData)
        setReferences(response.references ?? EMPTY_REFERENCES)
      } catch (err) {
        if (cancelled) return
        console.error('Failed to fetch audit history:', err)
        setError(err instanceof Error ? err : new Error('Failed to fetch audit history'))
        setAuditHistory([])
        setReferences(EMPTY_REFERENCES)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchAuditHistory()

    return () => {
      cancelled = true
    }
  }, [recordId, enabled, engagementApi])

  return {
    auditHistory,
    references,
    loading,
    error,
  }
}

